import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import ComsErrorBanner from '../components/ComsErrorBanner';
import {
  buildChannelSpaceUrl,
  getChannelDiagnostics,
  isComsHubUser,
  loadComsHubData,
} from '../utils/comsHub';
import './ComsHubPage.css';

const ComsHubPage = () => {
  const { currentUser } = useContext(AuthContext);
  const [connectorConfig, setConnectorConfig] = useState(null);
  const [defaultComsappBaseUrl, setDefaultComsappBaseUrl] = useState('');
  const [myThreads, setMyThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [hubError, setHubError] = useState('');
  const [hubErrorCode, setHubErrorCode] = useState('');
  const [provisionStatus, setProvisionStatus] = useState(null);

  const retryLoadHub = () => {
    setHubError('');
    setHubErrorCode('');
    loadComsHubData({
      api,
      currentUser,
      setConnectorConfig,
      setDefaultComsappBaseUrl,
      setMyThreads,
      setThreadsLoading,
    })
      .then((result) => {
        setProvisionStatus(result?.provisionStatus || null);
      })
      .catch((err) => {
        const code = err.response?.data?.code || '';
        const message = err.response?.data?.message || 'Could not load Coms Hub.';
        setHubErrorCode(code);
        setHubError(message);
      });
  };

  useEffect(() => {
    retryLoadHub();
  }, [currentUser]);

  const devSpaceUrl = buildChannelSpaceUrl(connectorConfig, defaultComsappBaseUrl, 'dev');
  const staffSpaceUrl = buildChannelSpaceUrl(connectorConfig, defaultComsappBaseUrl, 'staff');
  const devSpaceName = connectorConfig?.channels?.dev?.spaceName || connectorConfig?.channels?.dev?.label || 'Dev Space';
  const staffSpaceName = connectorConfig?.channels?.staff?.spaceName || connectorConfig?.channels?.staff?.label || 'Staff Space';
  const channelDiagnostics = getChannelDiagnostics(connectorConfig, defaultComsappBaseUrl, ['staff', 'dev']);

  if (!isComsHubUser(currentUser?.userType)) {
    return (
      <div className="coms-hub-page">
        <section className="account-card">
          <h1 className="account-card-title">Coms Hub</h1>
          <p className="account-subtitle">Your account does not currently have ComSapp workspace access.</p>
        </section>
      </div>
    );
  }

  return (
    <div className="coms-hub-page">
      <div className="account-header">
        <h1>Coms Hub</h1>
        <p className="account-subtitle">Staff-first communications hub for internal operations and dev handoff.</p>
      </div>

      <section className="account-card">
        <h2 className="account-card-title">Spaces</h2>
        {hubError && <ComsErrorBanner code={hubErrorCode} message={hubError} onRetry={retryLoadHub} />}

        {provisionStatus && (
          <div className={`coms-status-card coms-status-card--${provisionStatus.severity}`}>
            <strong>{provisionStatus.title}</strong>
            {provisionStatus.detail && <p>{provisionStatus.detail}</p>}
          </div>
        )}

        <div className="coms-channel-status-list">
          {channelDiagnostics.map((item) => (
            <div key={item.channelKey} className={`coms-channel-status coms-channel-status--${item.severity}`}>
              <div>
                <strong>{item.label}</strong>
                <span>{item.status}</span>
              </div>
              <p>{item.detail}</p>
            </div>
          ))}
        </div>

        <div className="dev-note-actions">
          {devSpaceUrl && (
            <a href={devSpaceUrl} target="_blank" rel="noreferrer" className="btn-edit-profile dev-note-link">
              Open {devSpaceName}
            </a>
          )}
          {staffSpaceUrl && (
            <a href={staffSpaceUrl} target="_blank" rel="noreferrer" className="btn-change-pw dev-note-link">
              Open {staffSpaceName}
            </a>
          )}
          <Link to="/dev-notes" className="btn-change-pw dev-note-link">Open Note Archive</Link>
          <Link to="/account" className="btn-change-pw dev-note-link">My Account</Link>
        </div>

        {!devSpaceUrl && !staffSpaceUrl && (
          <p className="my-threads-empty">No ComSapp channels are configured yet. Ask an admin to verify and enable Dev/Staff channels.</p>
        )}
      </section>

      <section className="account-card">
        <h2 className="account-card-title">My Threads</h2>
        {threadsLoading ? (
          <p className="my-threads-empty">Loading threads...</p>
        ) : myThreads.length === 0 ? (
          <p className="my-threads-empty">No threads yet. Start a conversation in your ComSapp space.</p>
        ) : (
          <ul className="my-threads-list">
            {myThreads.map((thread) => (
              <li key={thread.id} className="my-thread-item">
                <a
                  href={thread.deepLink || devSpaceUrl || staffSpaceUrl || '#'}
                  target="_blank"
                  rel="noreferrer"
                  className="my-thread-subject"
                >
                  {thread.locked && <span className="my-thread-locked-icon" title="Closed">🔒</span>}
                  {thread.subject}
                </a>
                <span className="my-thread-meta">
                  {thread.messageCount} msg{thread.messageCount !== 1 ? 's' : ''}
                  {thread.locked && ' · closed'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default ComsHubPage;
